import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PromoOffer, PromoDetailProduct } from '@/mocks/promos';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';

interface ProductCardProps {
  product: PromoDetailProduct;
  offer: PromoOffer;
}

const ProductCard = ({ product, offer }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [added, setAdded] = useState(false);
  const fav = isFavorite(product.id);

  const handleAdd = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      className="group rounded-2xl overflow-hidden transition-all duration-300 flex flex-col"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: 200 }}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Discount badge */}
        <div
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full font-orbitron font-black text-xs"
          style={{ background: offer.color, color: '#050508', boxShadow: `0 0 10px ${offer.color}60` }}
        >
          -{product.discount}%
        </div>

        {/* Out of stock */}
        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="font-mono text-xs text-white/60 px-3 py-1.5 rounded-full border border-white/20">
              Нет в наличии
            </span>
          </div>
        )}

        {/* Fav button */}
        <button
          onClick={() => toggleFavorite(product.id)}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
          style={{
            background: fav ? `${offer.color}20` : 'rgba(0,0,0,0.5)',
            border: fav ? `1px solid ${offer.color}50` : '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <i
            className={fav ? 'ri-heart-fill' : 'ri-heart-line'}
            style={{ color: fav ? offer.color : 'rgba(255,255,255,0.5)', fontSize: 14 }}
          />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Category */}
        <span
          className="font-mono text-[10px] mb-2 px-2 py-0.5 rounded-full self-start"
          style={{ background: `${offer.color}12`, color: offer.color, border: `1px solid ${offer.color}25` }}
        >
          {product.category}
        </span>

        <Link to={`/product/${product.id}`} className="group/link">
          <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-1 group-hover/link:opacity-75 transition-opacity line-clamp-2">
            {product.name}
          </h3>
        </Link>
        <p className="font-mono text-xs text-white/35 mb-3 line-clamp-1">{product.description}</p>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <i
                key={s}
                className={s <= Math.round(product.rating) ? 'ri-star-fill' : 'ri-star-line'}
                style={{ color: '#f0ff00', fontSize: 11 }}
              />
            ))}
          </div>
          <span className="font-mono text-[10px] text-white/35">{product.rating} ({product.reviews.toLocaleString()})</span>
        </div>

        {/* Price */}
        <div className="flex items-end gap-2 mb-4 mt-auto">
          <span
            className="font-orbitron font-black text-xl"
            style={{ color: offer.color, textShadow: `0 0 10px ${offer.color}50` }}
          >
            {product.price.toLocaleString()} ₽
          </span>
          <span className="font-mono text-xs text-white/30 line-through mb-0.5">
            {product.oldPrice.toLocaleString()} ₽
          </span>
        </div>

        {/* Add to cart */}
        <button
          onClick={handleAdd}
          disabled={!product.inStock}
          className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          style={
            added
              ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 14px rgba(57,255,20,0.5)' }
              : product.inStock
              ? { background: offer.color, color: '#050508', boxShadow: `0 0 14px ${offer.color}40` }
              : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)' }
          }
        >
          <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'} />
          {added ? 'Добавлено!' : product.inStock ? 'В корзину' : 'Нет в наличии'}
        </button>
      </div>
    </div>
  );
};

interface PromoProductListProps {
  offer: PromoOffer;
  products: PromoDetailProduct[];
}

const PromoProductList = ({ offer, products }: PromoProductListProps) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.05 });
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'default' | 'price_asc' | 'price_desc' | 'discount'>('default');

  const categories = ['all', ...Array.from(new Set(products.map((p) => p.category)))];

  const filtered = products
    .filter((p) => activeCategory === 'all' || p.category === activeCategory)
    .sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'discount') return b.discount - a.discount;
      return 0;
    });

  return (
    <section
      id="products"
      ref={ref as React.RefObject<HTMLElement>}
      className="px-6 lg:px-12 py-12"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg"
                style={{ background: `${offer.color}15`, border: `1px solid ${offer.color}30` }}
              >
                <i className="ri-shopping-bag-line text-sm" style={{ color: offer.color }} />
              </div>
              <h2 className="font-orbitron font-bold text-xl text-white">Товары по акции</h2>
            </div>
            <p className="font-mono text-xs text-white/35">
              {filtered.length} из {products.length} товаров
            </p>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-white/35">Сортировка:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="font-mono text-xs px-3 py-2 rounded-lg cursor-pointer outline-none"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              <option value="default">По умолчанию</option>
              <option value="price_asc">Цена: по возрастанию</option>
              <option value="price_desc">Цена: по убыванию</option>
              <option value="discount">По размеру скидки</option>
            </select>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 flex-wrap mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="whitespace-nowrap px-4 py-2 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
              style={
                activeCategory === cat
                  ? { background: offer.color, color: '#050508', boxShadow: `0 0 12px ${offer.color}50` }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }
              }
            >
              {cat === 'all' ? 'Все категории' : cat}
              {cat === 'all' && (
                <span className="ml-1.5 opacity-60">({products.length})</span>
              )}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((product, i) => (
            <div
              key={product.id}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 0.05}s, transform 0.5s ease ${i * 0.05}s`,
              }}
            >
              <ProductCard product={product} offer={offer} />
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-search-line text-4xl text-white/20 mb-3 block" />
            <p className="font-mono text-sm text-white/30">Товары в этой категории не найдены</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoProductList;
